/* global paypal */
import { Controller } from '@hotwired/stimulus'
import { put } from '../lib/request'
import { emit } from '../lib/events'

// Manages the PayPal checkout flow. Renders the PayPal button, handles order
// creation, payment capture, and completion. Emits 'paypalEnabled' to notify
// the Stripe controller to unmount its elements when PayPal is selected.
export default class extends Controller {
  static targets = ['button']
  static values = { updateUrl: String }

  // Initializes the PayPal button when the target element connects.
  buttonTargetConnected = () => {
    this.setupPaypal()
  }

  // Configures and renders the PayPal Buttons component.
  // createOrder: builds the PayPal order with the current cart total and emits
  //   'paypalEnabled' to unmount Stripe elements (prevents background updates on mobile Safari).
  // onApprove: captures the payment and calls completeOrder to finalize.
  // onCancel: reloads the page to reset checkout state.
  // onError: records the error to the order and reloads.
  setupPaypal = () => {
    paypal.Buttons({
      fundingSource: paypal.FUNDING.PAYPAL,
      style: {
        color: 'blue',
        shape: 'pill',
        height: 40,
        label: 'pay',
      },
      createOrder: (data, actions) => {
        emit('paypalEnabled', { bubbles: true })

        return actions.order.create(this.orderObject)
      },
      onApprove: (data, actions) => {
        return actions.order.capture().then((details) => {
          this.completeOrder(details)
        })
      },
      onCancel: () => {
        return location.reload()
      },
      onError: (error) => {
        return this.handleErrors(error)
      },
    }).render(this.buttonTarget)
  }

  // Sends the completion request to the server with the PayPal transaction details.
  // Retries up to 3 times with linear backoff (1s, 2s, 3s) since the payment has
  // already been captured by PayPal at this point — we must persist it.
  // Falls back to page reload if all retries fail.
  async completeOrder(details) {
    const baseParams = {
      transaction_id: details.id,
      payer_id: details.payer.payer_id,
      payment_id: details.purchase_units[0].payments.captures[0].id,
      provider: 'paypal',
      status: 'complete',
    }
    const params = { order: baseParams }

    const maxRetries = 3
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await put(this.updateUrlValue, {
          body: JSON.stringify(params),
          responseKind: 'json',
        })

        if (response.ok) {
          const json = await response.json
          window.location.href = json.url
          return
        }
      }
      catch { /* retry */ }

      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)))
      }
    }

    location.reload()
  }

  // Records a PayPal error to the order on the server and reloads.
  // Guards against PayPal passing non-standard error objects (strings, undefined).
  handleErrors = async (error) => {
    const message = (error && error.message) ? error.message : 'Payment failed'
    const params = {
      order: {
        checkout_errors: [message],
        provider: 'paypal',
        status: 'error',
      },
    }

    try {
      await put(this.updateUrlValue, {
        body: JSON.stringify(params),
        responseKind: 'turbo-stream',
      })
    }
    catch { /* noop */ }

    location.reload()
  }

  // Builds the PayPal order object with the current cart total.
  // Shipping is handled by Stripe's address element, so PayPal
  // shipping collection is disabled via NO_SHIPPING.
  get orderObject() {
    return {
      purchase_units: [
        {
          amount: {
            currency_code: 'USD',
            value: this.totalValue,
          },
        },
      ],
      application_context: {
        shipping_preference: 'NO_SHIPPING',
      },
    }
  }

  // Reads the current order total from the DOM.
  get totalValue() {
    const totalElement = document.querySelector('#total-price')

    if (totalElement) {
      const stripCurrencyFormat = totalElement.innerHTML.replace('$', '')
      return Number(stripCurrencyFormat)
    }
    else {
      return 0
    }
  }
}
